import Service from '@ember/service';
import Quill from 'quill';

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      service — registry mapping a name to a live Quill editor instance,
 *              plus by-name proxies for the common Quill operations (content
 *              read/write, formatting, selection, focus)
 *   extends:   Service (@ember/service)
 *   template:  none
 *   key:       register(name, instance), deregister(name, instance),
 *              instance(name), plus by-name proxies: deleteText, getContents,
 *              getLength, getText, insertEmbed, insertText, setContents, setText,
 *              updateContents, format, formatLine, formatText, getFormat,
 *              removeFormat, getBounds, getSelection, blur, disable, enable,
 *              focus, hasFocus, update
 *   uses:      quill (Quill.sources)
 *   siblings:  -
 *   tests:     tests/unit/services/quill-test.js
 *   gotchas:   register() is last-writer-wins by design — on a same-pass remount
 *              Glimmer runs the new did-insert before the old destructor, so
 *              refusing a taken name orphaned the live editor; deregister() only
 *              deletes when the passed instance still matches what's stored, so a
 *              late/stale teardown can't clobber a freshly re-registered editor
 *              under the same name.
 */
class QuillService extends Service {
  constructor(...args) {
    super(...args);
    this.instances = Object.create(null);
  }
  /**
   * Register a Quill instance by name. The LAST writer wins.
   *
   * It used to refuse when the name was taken, to avoid orphaning the previous
   * editor. That inverted the failure: on a same-pass remount (a host swapping
   * the component that owns a fixed @id) Glimmer runs the new did-insert BEFORE
   * the old component's destructor, so the refusal kept the DEAD editor in the
   * map and the old teardown's deregister — whose instance check then matched —
   * emptied the name entirely. The live editor ended up unreachable for good.
   * Overwriting cannot lose an editor: the displaced one is being torn down, and
   * its deregister is a no-op because the stored instance no longer matches.
   */
  register(name, instance) {
    this.instances[name] = instance;
  }

  /**
   * De-register a Quill instance by name.
   *
   * Deletes ONLY when the stored instance is the caller's, so a late teardown
   * can't clobber a freshly registered editor reusing the same name. The caller
   * must pass its instance: the old `instance === undefined` shortcut deleted
   * unconditionally, which let `deregister(name)` wipe somebody else's live
   * registration.
   */
  deregister(name, instance) {
    if (instance && this.instances[name] === instance) {
      delete this.instances[name];
    }
  }

  /**
   * Get a Quill instance by name.
   *
   * @returns {(Quill|null)}
   */
  instance(name) {
    return this.instances[name] ?? null;
  }

  /**
   * Delete text from the named editor.
   */
  deleteText(name, index, length, source = Quill.sources.API) {
    if (this.instances[name]) {
      return this.instances[name].deleteText(index, length, source);
    }
    return null;
  }

  /**
   * Retrieve the contents of the named editor.
   */
  getContents(name) {
    if (this.instances[name]) {
      return this.instances[name].getContents();
    }
    return null;
  }

  /**
   * Retrieve the length of the editor contents.
   */
  getLength(name) {
    if (this.instances[name]) {
      return this.instances[name].getLength();
    }
    return null;
  }

  /**
   * Retrieve the string content of the editor.
   */
  getText(name, index = 0, length = undefined) {
    if (this.instances[name]) {
      return this.instances[name].getText(index, length);
    }
    return null;
  }

  /**
   * Insert embedded content into the named editor.
   */
  insertEmbed(name, index, type, value, source = Quill.sources.API) {
    if (this.instances[name]) {
      return this.instances[name].insertEmbed(index, type, value, source);
    }
    return null;
  }

  /**
   * Insert text into the named editor.
   */
  insertText(name, ...args) {
    if (this.instances[name]) {
      return this.instances[name].insertText(...args);
    }
    return null;
  }

  /**
   * Set contents on the named editor.
   */
  setContents(name, delta, source = Quill.sources.API) {
    if (this.instances[name]) {
      return this.instances[name].setContents(delta, source);
    }
    return null;
  }

  /**
   * Set text contents on the named editor.
   */
  setText(name, text, source = Quill.sources.API) {
    if (this.instances[name]) {
      return this.instances[name].setText(text, source);
    }
    return null;
  }

  /**
   * Apply delta to the named editor's contents.
   */
  updateContents(name, delta, source = Quill.sources.API) {
    if (this.instances[name]) {
      return this.instances[name].updateContents(delta, source);
    }
    return null;
  }

  /**
   * Format text at the user's current selection in the named editor.
   */
  format(name, formatName, value, source = Quill.sources.API) {
    if (this.instances[name]) {
      return this.instances[name].format(formatName, value, source);
    }
    return null;
  }

  /**
   * Formats all lines in a given range in the named editor.
   */
  formatLine(name, ...args) {
    if (this.instances[name]) {
      return this.instances[name].formatLine(...args);
    }
    return null;
  }

  /**
   * Format text in the named editor.
   */
  formatText(name, ...args) {
    if (this.instances[name]) {
      return this.instances[name].formatText(...args);
    }
    return null;
  }

  /**
   * Retrieves common formatting of the text in the named editor.
   */
  getFormat(name, ...args) {
    if (this.instances[name]) {
      return this.instances[name].getFormat(...args);
    }
    return null;
  }

  /**
   * Removes all formatting and embeds within the given range.
   */
  removeFormat(name, index, length, source = Quill.sources.API) {
    if (this.instances[name]) {
      return this.instances[name].removeFormat(index, length, source);
    }
    return null;
  }

  /**
   * Retrieves the pixel position and dimensions of a selection.
   */
  getBounds(name, index, length = 0) {
    if (this.instances[name]) {
      return this.instances[name].getBounds(index, length);
    }
    return null;
  }

  /**
   * Retrieves the user's selection range in the named editor.
   */
  getSelection(name, focus = false) {
    if (this.instances[name]) {
      return this.instances[name].getSelection(focus);
    }
    return null;
  }

  /**
   * Remove focus from the named editor.
   */
  blur(name) {
    if (this.instances[name]) {
      this.instances[name].blur();
    }
  }

  /**
   * Disable the named editor.
   */
  disable(name) {
    if (this.instances[name]) {
      this.instances[name].disable();
    }
  }

  /**
   * Enable the named editor.
   */
  enable(name) {
    if (this.instances[name]) {
      this.instances[name].enable();
    }
  }

  /**
   * Focuses the named editor.
   */
  focus(name) {
    if (this.instances[name]) {
      this.instances[name].focus();
    }
  }

  /**
   * Does the named editor have focus?
   */
  hasFocus(name) {
    if (this.instances[name]) {
      return this.instances[name].hasFocus();
    }
    return null;
  }

  /**
   * Synchronously check the named editor for user updates.
   */
  update(name, source = Quill.sources.USER) {
    if (this.instances[name]) {
      this.instances[name].update(source);
    }
  }
}

export { QuillService as default };
//# sourceMappingURL=quill.js.map
